import React, { useEffect, useState, useCallback } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import crm, { crmUser } from '../crm/api';

const TABS = [
  { key: 'source', label: 'Lead source' },
  { key: 'reps', label: 'Rep performance' },
  { key: 'products', label: 'Product demand' },
];

// Presets are resolved against the browser's today; blank range = all time.
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const PRESETS = [
  { label: 'This week', from: () => daysAgo(7), to: () => iso(new Date()) },
  { label: '30 days', from: () => daysAgo(30), to: () => iso(new Date()) },
  { label: '90 days', from: () => daysAgo(90), to: () => iso(new Date()) },
  { label: 'All time', from: () => '', to: () => '' },
];

const amount = (n) => Number(n || 0).toLocaleString('en-IN');
// jsPDF's built-in Helvetica is Latin-1 — it has no ₹ (U+20B9) and renders it as
// mojibake. Web keeps the symbol; the PDF spells it "Rs.".
const cell = (row, c, forPdf) => {
  const v = row[c.key];
  if (c.money) return (forPdf ? 'Rs. ' : '₹') + amount(v);
  if (c.suffix) return `${Number(v) || 0}${c.suffix}`;
  return v === undefined || v === null || v === '' ? '—' : String(v);
};

export default function Reports() {
  // A rep is the only row in "Rep performance", so that tab is admin-only.
  const isAdmin = (crmUser() || {}).role === 'admin';
  const tabs = isAdmin ? TABS : TABS.filter((t) => t.key !== 'reps');
  const [tab, setTab] = useState('source');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    crm.get(`/reports/${tab}`, { params: { from: from || undefined, to: to || undefined } })
      .then(({ data }) => setRep(data))
      .catch((e) => setErr(e.response?.data?.error || 'Could not load this report.'))
      .finally(() => setLoading(false));
  }, [tab, from, to]);
  useEffect(load, [load]);

  const rangeText = from || to ? `${from || 'start'} to ${to || 'today'}` : 'All time';

  function exportPdf() {
    if (!rep) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();

    // header band
    doc.setFillColor(11, 110, 62);
    doc.rect(0, 0, W, 62, 'F');
    doc.setTextColor(255).setFont('helvetica', 'bold').setFontSize(15);
    doc.text('Diabetik Foot Care India', 40, 27);
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(200, 230, 212);
    doc.text('Lead Cloud — ' + rep.title, 40, 44);
    doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString('en-IN')}`, W - 40, 44, { align: 'right' });

    doc.setTextColor(70).setFontSize(10);
    doc.text(`${rep.subtitle}  ·  Period: ${rangeText}  ·  Scope: ${rep.scope}`, 40, 84);

    autoTable(doc, {
      startY: 98,
      head: [rep.columns.map((c) => c.label)],
      body: rep.rows.map((r) => rep.columns.map((c) => cell(r, c, true))),
      foot: [rep.columns.map((c, i) => (i === 0 ? 'Total' : cell(rep.totals, c, true)))],
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [11, 110, 62], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [237, 243, 239], textColor: 20, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 251, 249] },
      margin: { left: 40, right: 40 },
      didDrawPage: () => {
        const H = doc.internal.pageSize.getHeight();
        doc.setFontSize(8).setTextColor(150);
        doc.text('DFC Lead Cloud · confidential', 40, H - 20);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, W - 40, H - 20, { align: 'right' });
      },
    });

    doc.save(`DFC-${rep.key}-report-${from || 'all'}-${to || 'today'}.pdf`);
  }

  return (
    <div className="space-y-5 max-w-[1180px] pt-1">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[22px] font-extrabold text-[var(--heading)] tracking-tight">{isAdmin ? 'Reports' : 'My performance'}</h2>
          <p className="text-[13px] text-[var(--muted)]">{rep?.subtitle || 'Lead performance across sources, reps and products.'}</p>
        </div>
        <button onClick={exportPdf} disabled={!rep || loading}
          className="flex items-center gap-2 acc-bg text-white text-[13px] font-bold px-4 py-2.5 rounded-xl disabled:opacity-50">
          <FileDown size={16} /> Export PDF
        </button>
      </div>

      {/* tabs */}
      <div className="inline-flex rounded-xl p-1" style={{ background: 'var(--soft)' }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="px-4 py-1.5 rounded-lg text-[13px] font-bold transition-colors"
            style={tab === t.key ? { background: 'var(--card)', color: 'var(--acc2)', boxShadow: '0 1px 3px rgba(0,0,0,.12)' } : { color: 'var(--muted)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* range */}
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button key={p.label} onClick={() => { setFrom(p.from()); setTo(p.to()); }}
            className="text-[12px] font-bold px-3 py-1.5 rounded-lg border border-[var(--border)] text-[var(--muted)] hover:bg-[var(--soft)]">
            {p.label}
          </button>
        ))}
        <span className="text-[12px] text-[var(--muted)] ml-1">From</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="text-[12.5px] rounded-lg px-2.5 py-1.5 border border-[var(--border)] bg-[var(--card)] text-[var(--text)]" />
        <span className="text-[12px] text-[var(--muted)]">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="text-[12.5px] rounded-lg px-2.5 py-1.5 border border-[var(--border)] bg-[var(--card)] text-[var(--text)]" />
        <span className="text-[12px] text-[var(--muted)] ml-auto">Period: <b className="text-[var(--text)]">{rangeText}</b></span>
      </div>

      {/* table */}
      <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-[var(--muted)] text-sm">
            <Loader2 size={15} className="animate-spin" /> Building report…
          </div>
        ) : err ? (
          <div className="p-10 text-center text-sm" style={{ color: '#b91c1c' }}>{err}</div>
        ) : !rep?.rows?.length ? (
          <div className="p-10 text-center text-sm text-[var(--muted)]">No leads in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[var(--soft)]">
                  {rep.columns.map((c, i) => (
                    <th key={c.key} className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] ${i ? 'text-right' : 'text-left'}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rep.rows.map((r, ri) => (
                  <tr key={ri} className="border-t border-[var(--border)]">
                    {rep.columns.map((c, i) => (
                      <td key={c.key} className={`px-4 py-3 ${i ? 'text-right tabular-nums' : 'font-bold text-[var(--text)]'}`}
                        style={i ? { color: 'var(--text)' } : undefined}>{cell(r, c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--border)]" style={{ background: 'var(--soft)' }}>
                  {rep.columns.map((c, i) => (
                    <td key={c.key} className={`px-4 py-3 font-extrabold text-[var(--text)] ${i ? 'text-right tabular-nums' : 'text-left'}`}>
                      {i === 0 ? 'Total' : cell(rep.totals, c)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
